'use client';

import { useIdentity } from '@/lib/identity';
import SettingsDialog from './SettingsDialog';

/**
 * "Who are you?" — the one dialog in this product with no way out.
 *
 * Nobody signs in, so every user starts life as a name generated from their IP
 * address: "Amber Falcon", "Patient Cheetah". The console is full of them, and
 * they are placeholders — nobody can tell which of them is who, which makes the
 * Users tab, the sessions table and the request log a list of strangers. Until
 * somebody has a nickname AND a team number the page is not usable: this covers
 * it, holds the keyboard, and does not take no for an answer.
 *
 * THIS FILE IS NOW ONLY THE TRIGGER. What it puts on screen is SettingsDialog in
 * `mandatory` mode — the same component, the same two questions, the same team-0
 * rule and the same write that the gear in the header opens. It used to be a
 * second, near-identical form, which is exactly the arrangement where a rule
 * gets fixed in one copy and not the other; the nickname logic now exists once.
 * `mandatory` changes only the ways OUT — no ✕, no Escape, no backdrop, focus
 * held inside the card, and a z-index above everything else on the page.
 *
 * WHY IT HAS NO WAY OUT. Because the answer is required, and every optional
 * version of this dialog collects nothing: a note you can wave away gets waved
 * away. A reload is not an escape either — the SERVER decides whether to ask, on
 * every load, and it keeps saying yes until an answer is stored. See `gateOpen`
 * in lib/identity.
 *
 * WHERE IT IS MOUNTED, and why that matters more than it looks. On the
 * translator page, NOT in the root layout — the admin console is served by this
 * same app under this same layout (qwer.grittranslate.com/admin), and a modal
 * with no way out rendered there would lock the operator out of their own
 * dashboard. That mistake was made once already with the TrueMarch wall; see the
 * note in app/layout.tsx.
 *
 * WHAT IT IS NOT. It is not a sign-in and it grants nothing: there is no
 * password, no verification, and somebody who wants to type a different name
 * tomorrow on a different connection is a different user as far as this app is
 * concerned. It makes the console legible to the operator; it does not pretend
 * to be identity in the security sense.
 */
export default function IdentityGate() {
  const { gateOpen } = useIdentity();

  // `gateOpen` is false until the server has answered, so nothing renders in
  // that window — a fraction of a second of "who are you?" in front of somebody
  // who has already answered is worse than asking a beat later.
  if (!gateOpen) return null;

  /*
   * `onClose` is a no-op, deliberately.
   *
   * Nothing inside the dialog can reach it in this mode — there is no ✕, no
   * Escape binding and no backdrop handler — except the successful save, and
   * that has already set `gateOpen` false through the provider, which unmounts
   * this. Passing a no-op rather than a closer is what guarantees the wall
   * cannot be dismissed by any path added to the dialog later.
   */
  return <SettingsDialog mandatory onClose={() => {}} />;
}
